import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import type { UseFormReturn } from 'react-hook-form';
import type { FormMode } from '../types/schema';
import type { FormLayoutNode } from '../types/layout';
import type { EngineAction } from '../store/engineReducer';
import { ActionRegistry } from '@packages/table-builder/registry';
import { sanitizePayload, hydratePayload, toSubmitActionParams } from './payloadSanitizer';
import { notify } from '@packages/shared/utils';
import { showApiErrorToast } from '@packages/shared/utils/showApiErrorToast';
import { refreshTable } from '@packages/table-builder/utils/refreshTable';
import {
  ENTITY_REFRESH_EVENT,
  matchesEntityRefresh,
  refreshEntityData,
  type EntityRefreshDetail,
} from '@packages/table-builder/utils/invalidateActions';
import {
  globalCache,
  globalDedup,
  buildCacheKey,
  DEFAULT_POLICY,
  invalidateActionCache,
} from '@packages/cache-manager';
import { interpolateBlockParam } from '@packages/compose/registry';
import { buildFetchTriggerKey } from '../utils/buildFetchTriggerKey';

interface UseLifecycleParams {
  form: UseFormReturn<Record<string, unknown>>;
  mode: FormMode;
  modeName: string;
  schemaId: string;
  params?: Record<string, string>;
  meta?: Record<string, unknown>;
  layout: FormLayoutNode[];
  namespace?: string;
  hydratingRef: React.MutableRefObject<boolean>;
  hydrationEpochRef?: React.MutableRefObject<number>;
  dispatch: React.Dispatch<EngineAction>;
  onSuccess?: (result?: unknown) => void;
  onError?: (error: unknown) => void;
  onFatalError?: (error: unknown) => void;
  /** Pre-fetched data — when provided, hydrate directly. */
  initialValues?: Record<string, unknown>;
  /**
   * When true, initialValues is a warm-start from a different action than resolvedFetch.
   * The processor hydrates from initialValues immediately, then fetches the supplemental data.
   * Example: tax tab gets base participant data, then fetches FETCH_PARTICIPANT_TAX.
   */
  supplementalFetch?: boolean;
  /**
   * Called after hydrate completes and `hydratingRef` is cleared.
   * Used by the session to sync optionProp-derived flags (create/edit parity).
   */
  onHydrated?: () => void;
  /** Block-events callback — fires after successful submit. */
  onSubmitSuccess?: (payload: { result: unknown; values: Record<string, unknown>; mode: string }) => void;
  /** Block-events callback — fires after failed submit. */
  onSubmitFailure?: (payload: { error: unknown; mode: string }) => void;
  /** Block-events scope — threaded to ActionRegistry.executeData so action handlers can publish events. */
  eventScope?: import('@packages/block-events/core').EventScope | null;
  /** Semantic ID of this form block — passed to action context for event targeting. */
  formSemanticId?: string;
}

interface LifecycleReturn {
  handleSubmit: (values: Record<string, unknown>) => Promise<unknown>;
}

/** Hook point for PDF generation — external utility replaces this at integration time. */
export type GeneratePdfHook = (
  html: string,
  formValues: Record<string, unknown>
) => Promise<void> | void;

let generatePdfHook: GeneratePdfHook | null = null;

export function registerGeneratePdfHook(hook: GeneratePdfHook): void {
  generatePdfHook = hook;
}

export function getGeneratePdfHook(): GeneratePdfHook | null {
  return generatePdfHook;
}

const CONCURRENCY_ETAG_KEY = '__concurrencyETag';

export function useLifecycle({
  form,
  mode,
  modeName,
  schemaId,
  params,
  meta,
  layout,
  namespace,
  hydratingRef,
  hydrationEpochRef,
  dispatch,
  onSuccess,
  onError,
  onFatalError,
  initialValues,
  supplementalFetch,
  onHydrated,
  onSubmitSuccess,
  onSubmitFailure,
  eventScope,
  formSemanticId,
}: UseLifecycleParams): LifecycleReturn {
  const queryClient = useQueryClient();
  const fetchedAtRef = useRef<number | null>(null);
  const etagRef = useRef<string | null>(null);
  /** True after at least one successful hydrate — enables soft force-refetch (no spinner wipe). */
  const hasHydratedRef = useRef(false);
  const eventScopeRef = useRef(eventScope);
  eventScopeRef.current = eventScope;
  const formSemanticIdRef = useRef(formSemanticId);
  formSemanticIdRef.current = formSemanticId;

  const lifecycle = mode.lifecycle;
  const storageKey =
    lifecycle?.autoSave?.storageKey ?? `form-draft-${schemaId}-${params?.id ?? 'new'}`;

  // Resolve fetchActionId (simple string) into a FormFetch, falling back to dataSource.fetch.
  // fetchActionId auto-constructs params from the params prop — defaults to { id } for backward compat.
  const resolvedFetch = useMemo(() => {
    if (mode.fetchActionId) {
      return {
        actionId: mode.fetchActionId,
        params: params ?? {} as Record<string, string>,
      };
    }
    return mode.dataSource?.fetch ?? null;
  }, [mode.fetchActionId, mode.dataSource?.fetch, params]);

  const fetchTriggerKey = useMemo(
    () => buildFetchTriggerKey(resolvedFetch, params, meta),
    [resolvedFetch, params, meta],
  );

  const hydrateFromData = useCallback(
    (raw: Record<string, unknown>, _fetch: import('../types/schema').FormFetch) => {
      const etag = extractETag(raw);
      if (etag) etagRef.current = etag;

      // Form-shaped payload is the source of truth — no mapId transformers.
      // Data actions must return form field keys; hydratePayload only formats display values.
      const mapped = stripConcurrencyMeta(raw);
      const formValues = hydratePayload(mapped, layout, namespace);

      // form.reset() updates _defaultValues so Controllers mounting after this
      // (on the same batched render triggered by FETCH_SUCCESS) pick up values automatically.
      hydratingRef.current = true;
      form.reset(formValues);
      fetchedAtRef.current = Date.now();
      hasHydratedRef.current = true;

      requestAnimationFrame(() => {
        hydratingRef.current = false;
        if (hydrationEpochRef) hydrationEpochRef.current += 1;
        onHydrated?.();
      });
    },
    [form, layout, namespace, hydratingRef, hydrationEpochRef, onHydrated]
  );

  // When pre-fetched data is provided by a builder action, hydrate directly.
  // The initial fetch is skipped, but cache subscription (below) still activates
  // so that invalidateActionCache → re-fetch works after sibling edits.
  useEffect(() => {
    if (!initialValues) return;

    // Guard: skip re-hydration if the form was already hydrated from initialValues.
    // resolveStepBlock creates a new object reference on every render, causing this
    // effect to re-fire. The data is identical — re-running form.reset() is wasteful
    // and can cause visible field flicker.
    if (hasHydratedRef.current) return;

    // Wizard step data is already in form-shape (collected via form.getValues()).
    // It should NOT go through hydratePayload which expects raw API DTO keys.
    // This applies to BOTH single-object forms ({ field1, field2 }) and
    // repeatable/array forms ({ items: [{ field1, field2 }] }).
    // Primary: explicit marker from WizardBlockRendererV2 on back-navigation.
    // Fallback: heuristic for legacy paths without the marker.
    const isWizardRestore = !!(initialValues as Record<string, unknown>).__wizardRestore;
    const shouldBypassHydration = isWizardRestore || (
      Array.isArray(initialValues.items) && initialValues.items.length > 0
      && typeof initialValues.items[0] === 'object'
    );

    if (shouldBypassHydration) {
      const { __wizardRestore: _, ...cleanValues } = initialValues as Record<string, unknown> & { __wizardRestore?: boolean };
      hydratingRef.current = true;
      form.reset(cleanValues);
      hasHydratedRef.current = true;
      requestAnimationFrame(() => {
        hydratingRef.current = false;
        if (hydrationEpochRef) hydrationEpochRef.current += 1;
        onHydrated?.();
      });
      dispatch({ type: 'FETCH_SUCCESS' });
      return;
    }

    const fakeFetch = resolvedFetch ?? { actionId: '', params: {} };
    hydrateFromData(initialValues, fakeFetch as import('../types/schema').FormFetch);
    dispatch({ type: 'FETCH_SUCCESS' });
  }, [initialValues]);

  const runFetch = useCallback(
    (opts?: { force?: boolean }) => {
      // Skip fetch when pre-fetched data already covers this action.
      // force:true (cache invalidation) always proceeds — enables re-fetch after edits.
      if (initialValues && !opts?.force) return;
      if (!resolvedFetch) return;

      const resolvedParams: Record<string, unknown> = {};
      if (resolvedFetch.params) {
        for (const [key, value] of Object.entries(resolvedFetch.params)) {
          resolvedParams[key] = interpolateBlockParam(value, params, meta);
        }
      }

      const hasEmptyParam = Object.values(resolvedParams).some((v) => v === '');
      if (hasEmptyParam) {
        dispatch({ type: 'FETCH_SUCCESS' });
        return;
      }

      const cacheKey = buildCacheKey(resolvedFetch.actionId, resolvedParams as Record<string, string>);

      // Do NOT call globalCache.invalidate() here on force.
      // invalidateActionCache / invalidateByPrefix already notified subscribers; invalidating
      // again re-enters every subscriber → infinite sync loop → browser Aw Snap.

      const allowCache =
        !opts?.force && Boolean(resolvedFetch.staleWhileRevalidate);
      const cached = allowCache ? globalCache.get(cacheKey) : { status: 'miss' as const };

      const isStaleWhileRevalidate = Boolean(resolvedFetch.staleWhileRevalidate) && cached.status === 'stale';
      // Force-refetch after a successful hydrate must not wipe UI (Modal.Body scroll collapse).
      const softRefresh = Boolean(opts?.force && hasHydratedRef.current);
      const keepShowingData = softRefresh || isStaleWhileRevalidate;

      if (cached.status === 'fresh') {
        hydrateFromData(cached.data as Record<string, unknown>, resolvedFetch);
        dispatch({ type: 'FETCH_SUCCESS' });
        return;
      }

      if (!keepShowingData) {
        dispatch({ type: 'FETCH_START' });
      }

      if (isStaleWhileRevalidate && cached.data) {
        hydrateFromData(cached.data as Record<string, unknown>, resolvedFetch);
      }

      const existingData = supplementalFetch ? initialValues : undefined;

      globalDedup
        .dedupe(cacheKey, () =>
          ActionRegistry.executeData(resolvedFetch.actionId, resolvedParams as Record<string, string>, existingData),
        )
        .then((data) => {
          if (!data) {
            if (!keepShowingData) {
              dispatch({ type: 'FETCH_SUCCESS' });
            }
            return;
          }

          if ((allowCache || opts?.force) && Boolean(resolvedFetch.staleWhileRevalidate)) {
            globalCache.set(cacheKey, data, DEFAULT_POLICY);
          }

          // Supplemental fetch: merge new fields into existing form values (don't wipe base data).
          const mergedData = supplementalFetch && initialValues
            ? { ...initialValues, ...(data as Record<string, unknown>) }
            : data as Record<string, unknown>;
          hydrateFromData(mergedData, resolvedFetch);
          dispatch({ type: 'FETCH_SUCCESS' });
        })
        .catch((err) => {
          console.error('[lifecycleProcessor] fetch FAILED', err);
          if (keepShowingData) {
            onError?.({ type: 'fetch', error: err });
            return;
          }
          dispatch({ type: 'FETCH_ERROR', error: err });
          onError?.({ type: 'fetch', error: err });
          onFatalError?.({ type: 'fetch', error: err });
        });
    },
    [
      initialValues,
      resolvedFetch,
      modeName,
      params,
      meta,
      hydrateFromData,
      dispatch,
      onError,
      onFatalError,
    ],
  );

  // Initial fetch.
  // - No initialValues → normal fetch (shows spinner, loads data).
  // - initialValues + same action → skip (data is complete, just subscribe for invalidation).
  // - initialValues + supplementalFetch → force fetch (different action, needs extra data).
  //   e.g. tax tab: base participant renders instantly, FETCH_PARTICIPANT_TAX loads tax fields.
  useEffect(() => {
    if (initialValues) {
      if (supplementalFetch) runFetch({ force: true });
      return;
    }
    runFetch();
  // fetchTriggerKey encodes actionId + all interpolated fetch params (domain-agnostic).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runFetch/initialValues intentionally omitted (see comment block above)
  }, [fetchTriggerKey, supplementalFetch]);

  // Cache invalidation + entity refresh → force-refetch.
  // Active even when initialValues was provided — enables re-fetch after sibling edits.
  useEffect(() => {
    const fetchActionId = resolvedFetch?.actionId;
    if (!fetchActionId) return;

    const unsubCache = globalCache.subscribe(fetchActionId, () => {
      runFetch({ force: true });
    });

    const onEntityRefresh = (event: Event) => {
      const detail = (event as CustomEvent<EntityRefreshDetail>).detail;
      if (modeName !== 'view') return;
      if (!matchesEntityRefresh(detail, params?.id)) return;
      runFetch({ force: true });
    };

    window.addEventListener(ENTITY_REFRESH_EVENT, onEntityRefresh);
    return () => {
      unsubCache();
      window.removeEventListener(ENTITY_REFRESH_EVENT, onEntityRefresh);
    };
  }, [modeName, resolvedFetch?.actionId, params?.id, runFetch]);

  useEffect(() => {
    if (!lifecycle?.autoSave?.enabled) return;
    if (modeName === 'view') return;

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const draft = JSON.parse(stored);
        if (draft?.values && draft.schemaId === schemaId) {
          if (!lifecycle.autoSave.restorePrompt) {
            form.reset(draft.values);
          }
        }
      }
    } catch {
      /* ignore corrupt storage */
    }
  }, []);

  useEffect(() => {
    if (!lifecycle?.autoSave?.enabled) return;
    if (modeName === 'view') return;

    const debounceMs = lifecycle.autoSave.debounceMs ?? 2000;
    let timeout: ReturnType<typeof setTimeout>;

    const subscription = form.watch(() => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        try {
          localStorage.setItem(
            storageKey,
            JSON.stringify({
              values: form.getValues(),
              savedAt: Date.now(),
              schemaId,
              paramsId: params?.id,
            })
          );
        } catch {
          /* storage full — ignore */
        }
      }, debounceMs);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [lifecycle?.autoSave, form, storageKey, schemaId, params?.id, modeName]);

  useEffect(() => {
    if (!lifecycle?.dirtyGuard) return;
    // beforeunload is handled by FormRenderer — this section intentionally left for future hooks
  }, [lifecycle?.dirtyGuard]);

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      const submit = mode.dataSource?.submit;
      if (!submit) return;

      dispatch({ type: 'SUBMIT_START' });

      // Local-only submit: skip ActionRegistry, return values directly.
      // Used by filter forms and other client-side-only blocks.
      if (submit.actionId === '__LOCAL__') {
        dispatch({ type: 'SUBMIT_SUCCESS' });
        if (submit.successMessage) {
          notify.success(submit.successMessage);
        }
        onSubmitSuccess?.({ result: values, values, mode: modeName });
        onSuccess?.(values);
        return values;
      }

      const resolvedActionId = resolveSubmitActionId(submit, values);
      const effectiveSubmit =
        resolvedActionId !== submit.actionId ? { ...submit, actionId: resolvedActionId } : submit;

      const cleaned = sanitizePayload(values, layout, namespace, { mode: modeName });
      let actionParams = toSubmitActionParams(
        cleaned as Record<string, unknown> | Record<string, unknown>[],
        submit.constants
      );

      if (lifecycle?.concurrencyCheck && etagRef.current) {
        const body = actionParams.payload;
        if (!Array.isArray(body)) {
          actionParams = {
            payload: { ...body, [CONCURRENCY_ETAG_KEY]: etagRef.current },
          };
        }
      }
      try {
        const result = await ActionRegistry.executeData(
          effectiveSubmit.actionId,
          actionParams as unknown as Record<string, string>,
          undefined,
          { mode: modeName as 'create' | 'edit' | 'view', ...(eventScopeRef.current && { eventScope: eventScopeRef.current, formSemanticId: formSemanticIdRef.current }) },
        );

        const resultRecord = (result ?? {}) as Record<string, unknown>;

        // Handle 200 OK business failure: { success: false, errors: [...] }
        // Same pattern as table-builder useActionExecutor.executeSave
        if (isBusinessFailure(resultRecord)) {
          dispatch({ type: 'SUBMIT_ERROR', error: resultRecord });
          const fieldErrors = normalizeBusinessErrors(resultRecord, namespace);
          const hasInlineErrors = fieldErrors.some((e) => e.field);

          // Bare { success: false } with no errors/message = action already handled
          // the error (e.g. via safeApiCall + showApiErrorToast). Skip toasting but
          // still prevent success/close.
          const isBareFailure =
            !hasInlineErrors &&
            !resultRecord.message &&
            !resultRecord.errors;

          if (!isBareFailure) {
            if (hasInlineErrors && (lifecycle?.serverErrors ?? 'inline') === 'inline') {
              const isRepeatable = Boolean(mode.repeatable);
              let firstResolvedField: string | null = null;
              for (const err of fieldErrors) {
                if (!err.field) continue;
                let resolvedField = err.field;
                if (isRepeatable && err.sNo != null) {
                  const stripped = namespace && resolvedField.startsWith(`${namespace}.`)
                    ? resolvedField.slice(namespace.length + 1)
                    : resolvedField;
                  resolvedField = `items.${Number(err.sNo) - 1}.${stripped}`;
                }
                form.setError(resolvedField as any, { type: 'server', message: err.message });
                if (!firstResolvedField) firstResolvedField = resolvedField;
              }
              if (firstResolvedField) {
                requestAnimationFrame(() => {
                  const el =
                    document.querySelector(`[name="${firstResolvedField}"]`) ??
                    document.querySelector('[aria-invalid="true"]');
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    if (el instanceof HTMLElement) el.focus({ preventScroll: true });
                  }
                });
              }
            } else {
              const errorMsg = (resultRecord.message as string | undefined) ?? 'Validation failed.';
              if (hasInlineErrors) {
                for (const err of fieldErrors) {
                  notify.error(`${err.field}: ${err.message}`);
                }
              } else {
                notify.error(errorMsg, { autoClose: 30000 });
              }
            }
          }
          onError?.({ type: 'submit', error: resultRecord });
          throw new Error('SUBMIT_BUSINESS_ERROR');
        }

        const nextEtag = extractETag(resultRecord);
        if (nextEtag) etagRef.current = nextEtag;

        if (lifecycle?.autoSave?.enabled) {
          try {
            localStorage.removeItem(storageKey);
          } catch {
            /* ignore */
          }
        }

        dispatch({ type: 'SUBMIT_SUCCESS' });

        if (submit.successMessage) {
          notify.success(submit.successMessage);
        }

        const actionsToInvalidate = [
          ...(effectiveSubmit.invalidateActions ?? []),
          ...(effectiveSubmit.invalidateFetchActions ?? []),
        ].filter(Boolean);

        if (actionsToInvalidate.length) {
          invalidateActionCache(actionsToInvalidate);
        }

        if (effectiveSubmit.refreshEntities?.length) {
          for (const entity of effectiveSubmit.refreshEntities) {
            refreshTable(entity);
            refreshEntityData(entity, params?.id);
          }
        }

        if (effectiveSubmit.invalidateQueries?.length) {
          for (const queryKey of effectiveSubmit.invalidateQueries) {
            void queryClient.invalidateQueries(queryKey);
          }
        }

        if (effectiveSubmit.actionChain?.length) {
          for (const actionId of effectiveSubmit.actionChain) {
            await ActionRegistry.executeData(
              actionId,
              actionParams as unknown as Record<string, string>
            );
          }
        }

        if (effectiveSubmit.generatePdf) {
          const htmlContainer = document.querySelector('[data-form-html-template]');
          const html = htmlContainer?.innerHTML ?? '';
          const hook = getGeneratePdfHook();
          if (hook) {
            await hook(html, values);
          } else {
            console.info(
              '[form-engine] generatePdf requested — register hook via registerGeneratePdfHook()'
            );
          }
        }

        if (effectiveSubmit.navigate) {
          ActionRegistry.getNavigate()?.(effectiveSubmit.navigate);
        }

        const submitBehavior =
          lifecycle?.submitBehavior ?? (mode.dataSource?.fetch ? 'close' : 'reset');
        switch (submitBehavior) {
          case 'close':
            onSuccess?.(result);
            break;
          case 'stay':
            // Clear dirty; still notify host (e.g. EditParticipantModal closes via onSuccess).
            form.reset(form.getValues());
            onSuccess?.(result);
            break;
          case 'continue':
            // Like "stay" — clear dirty so subsequent edits trigger onDirtyChange again.
            // Host (e.g. Release/TransferAllocate) uses onDirtyChange to hide reactive tabs.
            form.reset(form.getValues());
            onSuccess?.(result);
            break;
          case 'reset':
            form.reset(mode.defaultValues ?? {});
            dispatch({ type: 'RESET' });
            onSuccess?.(result);
            break;
          case 'clearForm': {
            hydratingRef.current = true;
            const emptyValues =
              (form.formState.defaultValues as Record<string, unknown> | undefined) ??
              mode.defaultValues ??
              {};
            form.reset(emptyValues);
            dispatch({ type: 'RESET' });
            break;
          }
          default: {
            const _exhaustive: never = submitBehavior;
            void _exhaustive;
            onSuccess?.(result);
            break;
          }
        }

        onSubmitSuccess?.({ result, values, mode: modeName });

        return result;
      } catch (error) {
        if (error instanceof Error && error.message === 'SUBMIT_BUSINESS_ERROR') {
          onSubmitFailure?.({ error, mode: modeName });
          return;
        }

        dispatch({ type: 'SUBMIT_ERROR', error });
        onError?.({ type: 'submit', error });
        if (isHttpValidationError(error)) {
          const mapped = mapHttpValidationErrors(form, error, namespace);
          if (!mapped) {
            const mapped2 = mapAbpValidationErrors(form, error, namespace);
            if (!mapped2) {
              const messages = extractValidationMessages(error);
              notify.error(messages, { autoClose: 30000 });
            } else {
              scrollToFirstFormError(form);
            }
          } else {
            scrollToFirstFormError(form);
          }
        } else {
          showApiErrorToast(error);
        }
        onFatalError?.({ type: 'submit', error });
        onSubmitFailure?.({ error, mode: modeName });
        throw error;
      }
    },
    [
      mode.dataSource,
      mode.defaultValues,
      lifecycle,
      layout,
      namespace,
      form,
      dispatch,
      onSuccess,
      onSubmitSuccess,
      onSubmitFailure,
      onError,
      onFatalError,
      storageKey,
      queryClient,
    ]
  );

  return { handleSubmit };
}

function resolveSubmitActionId(
  submit: import('../types/schema').FormSubmit,
  values: Record<string, unknown>
): string {
  if (submit.actionIdMap && submit.actionIdKey) {
    const keyValue = getNestedSubmitValue(values, submit.actionIdKey);
    if (keyValue != null) {
      const mapped = submit.actionIdMap[String(keyValue)];
      if (mapped) return mapped;
    }
  }
  return submit.actionId;
}

function getNestedSubmitValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function extractETag(data: Record<string, unknown>): string | null {
  const etag = data.__etag ?? data._etag ?? data.etag;
  return etag != null ? String(etag) : null;
}

function stripConcurrencyMeta(data: Record<string, unknown>): Record<string, unknown> {
  const { __etag, _etag, etag, [CONCURRENCY_ETAG_KEY]: _removed, ...rest } = data;
  return rest;
}

function isHttpValidationError(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false;
  const raw = error as Record<string, any>;
  const status: number | undefined = raw?.status ?? raw?.response?.status;
  if (status !== 400) return false;
  const body = raw?.body ?? raw?.response?.data;
  const errorPayload = body?.error ?? body;
  if (errorPayload?.validationErrors != null) return true;
  if (Array.isArray(errorPayload?.errors)) return true;
  if (Array.isArray(body?.errors)) return true;
  return typeof errorPayload?.message === 'string' && errorPayload.message.length > 0;
}

function extractValidationMessages(error: unknown): string {
  const raw = error as Record<string, any>;
  const body = raw?.body ?? raw?.response?.data;
  const errorPayload = body?.error ?? body;
  if (errorPayload?.validationErrors != null) {
    return (errorPayload.validationErrors as Array<{ message: string }>)
      .map((e) => e.message)
      .join(', ');
  }
  if (Array.isArray(errorPayload?.errors)) {
    return (errorPayload.errors as Array<{ errorMessage?: string; message?: string }>)
      .map((e) => e.errorMessage ?? e.message ?? '')
      .filter(Boolean)
      .join(', ');
  }
  if (Array.isArray(body?.errors)) {
    return (body.errors as Array<{ errorMessage?: string; message?: string }>)
      .map((e) => e.errorMessage ?? e.message ?? '')
      .filter(Boolean)
      .join(', ');
  }
  return errorPayload?.message ?? 'Validation failed.';
}

/**
 * Resolve a server-returned field path to the namespaced form field path.
 *
 * Server returns PascalCase paths (e.g. "Email", "EmployeeId") while form
 * fields are camelCase with namespace prefix (e.g. "participant.email").
 *
 * Resolution order:
 * 1. Try the raw name as-is
 * 2. Try camelCase version
 * 3. Try with namespace prefix
 * 4. Try namespace + camelCase
 */
function resolveFieldPath(
  form: UseFormReturn<Record<string, unknown>>,
  serverPath: string,
  namespace?: string
): string {
  const camelCase = serverPath.charAt(0).toLowerCase() + serverPath.slice(1);

  const candidates: string[] = [serverPath];
  if (camelCase !== serverPath) candidates.push(camelCase);
  if (namespace) {
    candidates.push(`${namespace}.${serverPath}`);
    if (camelCase !== serverPath) candidates.push(`${namespace}.${camelCase}`);
  }

  const registered = form.getValues();
  for (const candidate of candidates) {
    if (getNestedSubmitValue(registered, candidate) !== undefined) {
      return candidate;
    }
  }

  // Fallback: prefer namespace.camelCase if namespace exists
  if (namespace) return `${namespace}.${camelCase}`;
  return camelCase;
}

/**
 * Check if a response body represents a 200 OK business failure.
 * Standard convention (aligned with table-builder): `{ success: false, errors?: [...] }`
 * Only `success` is checked — not `isSuccess` (legacy ABP flag removed).
 */
function isBusinessFailure(data: Record<string, unknown>): boolean {
  return data.success === false;
}

interface NormalizedFieldError {
  field: string;
  message: string;
  sNo?: string | null;
}

/**
 * Normalize business failure errors into field/message pairs.
 *
 * Single standard convention (aligned with table-builder GenericUiValidationErrorDto):
 *   `{ success: false, message?, errors: [{ fieldName, error, sNo?, uniqueIdentifier? }] }`
 *
 * Field name aliases resolved in order: fieldName → field → propertyName → property
 * Message aliases resolved in order:    error → errorMessage → message
 *
 * The legacy per-field `*ErrorMessage` DTO property convention is removed.
 * Actions must return `errors[]` from the API response — no manual field mapping in action classes.
 */
function normalizeBusinessErrors(
  data: Record<string, unknown>,
  namespace?: string
): NormalizedFieldError[] {
  const result: NormalizedFieldError[] = [];

  if (!Array.isArray(data.errors)) return result;

  for (const entry of data.errors as Array<Record<string, unknown>>) {
    const rawField =
      (entry.fieldName as string | undefined) ??
      (entry.field as string | undefined) ??
      (entry.propertyName as string | undefined) ??
      (entry.property as string | undefined);
    const message =
      (entry.error as string | undefined) ??
      (entry.errorMessage as string | undefined) ??
      (entry.message as string | undefined);
    if (rawField && message) {
      // If the field path already contains a dot, it is a fully-qualified RHF path
      // from action-level validation (e.g. "terminationRules.0.cancelShareTypeId").
      // Only prepend namespace for simple field names from API backend errors
      // (e.g. "CancelShareTypeId" → "namespace.cancelShareTypeId").
      const isFullyQualified = rawField.includes('.');
      const field = (namespace && !isFullyQualified)
        ? `${namespace}.${rawField.charAt(0).toLowerCase() + rawField.slice(1)}`
        : rawField.charAt(0).toLowerCase() + rawField.slice(1);
      result.push({ field, message, sNo: (entry.sNo as string) ?? null });
    }
  }

  return result;
}

/**
 * Map HTTP 400 errors array → form.setError per field.
 * Handles: { errors: [{ propertyName, errorMessage }] }
 * and:     { error: { ... errors: [...] } }
 */
function mapHttpValidationErrors(
  form: UseFormReturn<Record<string, unknown>>,
  error: unknown,
  namespace?: string
): boolean {
  const raw = error as Record<string, any>;
  const body = raw?.body ?? raw?.response?.data;
  const errorPayload = body?.error ?? body;
  const entries =
    (Array.isArray(errorPayload?.errors) ? errorPayload.errors : null) ??
    (Array.isArray(body?.errors) ? body.errors : null);

  if (!entries?.length) return false;

  let mapped = false;
  for (const entry of entries as Array<Record<string, unknown>>) {
    const rawFieldName =
      (entry.propertyName as string | undefined) ??
      (entry.field as string | undefined) ??
      (entry.property as string | undefined) ??
      (entry.fieldName as string | undefined);
    const message =
      (entry.errorMessage as string | undefined) ??
      (entry.message as string | undefined) ??
      (entry.error as string | undefined);
    if (rawFieldName && message) {
      const resolvedPath = resolveFieldPath(form, rawFieldName, namespace);
      form.setError(resolvedPath as any, { type: 'server', message });
      mapped = true;
    }
  }

  return mapped;
}

/**
 * Map ABP validationErrors (with members array) → form.setError per field.
 * ABP shape: { error: { validationErrors: [{ message, members: ['Email'] }] } }
 */
function mapAbpValidationErrors(
  form: UseFormReturn<Record<string, unknown>>,
  error: unknown,
  namespace?: string
): boolean {
  const raw = error as Record<string, any>;
  const body = raw?.body ?? raw?.response?.data;
  const errorPayload = body?.error ?? body;
  const validationErrors = errorPayload?.validationErrors;

  if (!Array.isArray(validationErrors) || validationErrors.length === 0) return false;

  let mapped = false;
  for (const ve of validationErrors as Array<{ message?: string; members?: string[] }>) {
    if (!ve.message) continue;
    if (ve.members?.length) {
      for (const member of ve.members) {
        const resolvedPath = resolveFieldPath(form, member, namespace);
        form.setError(resolvedPath as any, { type: 'server', message: ve.message });
        mapped = true;
      }
    }
  }

  return mapped;
}

function scrollToFirstFormError(form: UseFormReturn<Record<string, unknown>>): void {
  requestAnimationFrame(() => {
    const errorEl = document.querySelector('[aria-invalid="true"], .field-error, [data-error="true"]');
    if (errorEl) {
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (errorEl instanceof HTMLElement) errorEl.focus({ preventScroll: true });
      return;
    }
    const allErrors = form.formState.errors;
    const firstKey = findFirstErrorKey(allErrors as Record<string, unknown>);
    if (firstKey) {
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el instanceof HTMLElement) el.focus({ preventScroll: true });
      }
    }
  });
}

function findFirstErrorKey(errors: Record<string, unknown>, prefix = ''): string | null {
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    if (val == null) continue;
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof val === 'object' && 'message' in (val as object)) {
      return fullKey;
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      const nested = findFirstErrorKey(val as Record<string, unknown>, fullKey);
      if (nested) return nested;
    }
    if (Array.isArray(val)) {
      for (let i = 0; i < val.length; i++) {
        if (val[i] && typeof val[i] === 'object') {
          const nested = findFirstErrorKey(val[i] as Record<string, unknown>, `${fullKey}.${i}`);
          if (nested) return nested;
        }
      }
    }
  }
  return null;
}
